import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CreditCard, Smartphone, Building2 } from "lucide-react";
import { toast } from "sonner";

const formatCheckoutINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// Temporary launch pause: change to false when ready to launch.
const CHECKOUT_COMING_SOON = true;

export default function Checkout() {
  const { items, refreshCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [updatingCart, setUpdatingCart] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [reservationError, setReservationError] = useState("");

  useEffect(() => {
    let active = true;
    if (!user) return;
    api.get("/checkout/reservation")
      .then(({ data }) => {
        if (active) {
          setPendingPayment(data.reservation || null);
          setReservationError("");
        }
      })
      .catch((error) => {
        if (active) setReservationError(
          error.response?.data?.detail ||
          "Could not check previous payments. Check My Orders before retrying."
        );
      });
    return () => { active = false; };
  }, [user]);

  const eligibleItems = items.filter(
    (item) => item.checkout_eligible === true && item.line_id
  );
  const hasAvailableItems = eligibleItems.length > 0;
  const cartTotal = eligibleItems.reduce(
    (sum, item) => sum + Number(item.product?.price || 0) * item.quantity,
    0
  );
  const cartRevision = JSON.stringify(items.map((item) => [
    item.line_id, item.product_id, item.variant_sku, item.quantity,
    item.product?.price, item.product?.gst_rate,
    item.availability, item.available_quantity,
  ]));
  const selectionKey = JSON.stringify(
    eligibleItems.map((item) => item.line_id)
  );


  useEffect(() => {
    if (!reservation) return;
    const update = () => setSecondsLeft(
      Math.max(0, Math.ceil(
        (reservation.durationMs - (performance.now() - reservation.startedAt)) / 1000
      ))
    );
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [reservation]);
  const [coupon, setCoupon] = useState("");
  const [savedCoupon, setCouponApplied] = useState(null);
  const couponApplied =
    savedCoupon?._cartRevision === cartRevision ? savedCoupon : null;
  const [couponError, setCouponError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [codCheck, setCodCheck] = useState({ checking: false, available: null, reason: "" });
  const [shippingQuote, setShippingQuote] = useState(null);
  const [shippingQuoteStatus, setShippingQuoteStatus] = useState({
    checking: false,
    error: "",
  });
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    gstin: "",
  });

  useEffect(() => {
    loadRazorpay();
  }, []);

  useEffect(() => {
    if (user) refreshCart();
  }, [user, refreshCart]);

  const changeCheckoutQuantity = async (item, quantity) => {
    if (updatingCart || loading) return;
    setUpdatingCart(true);
    setShippingQuote(null);
    setCouponApplied(null);
    setCouponError("");
    try {
      await api.post("/cart/update", {
        product_id: item.product_id,
        variant_sku: item.variant_sku || null,
        quantity,
      });
      await refreshCart();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update your cart."
      );
    } finally {
      setUpdatingCart(false);
    }
  };


  // Reject eligibility responses for an older pincode.
  useEffect(() => {
    let cancelled = false;
    if (!hasAvailableItems || !/^\d{6}$/.test(form.pincode)) {
      setCodCheck({ checking: false, available: null, reason: "" });
      setPaymentMethod("razorpay");
      return;
    }
    setCodCheck({ checking: true, available: null, reason: "" });
    setPaymentMethod("razorpay");
    const timer = setTimeout(() => {
      api.post("/cod/check", { pincode: form.pincode })
        .then(({ data }) => {
          if (!cancelled) setCodCheck({
            checking: false,
            available: data.available === true,
            reason: data.reason || "",
          });
        })
        .catch(() => {
          if (!cancelled) setCodCheck({
            checking: false,
            available: false,
            reason: "Could not check cash-on-delivery availability.",
          });
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.pincode, hasAvailableItems]);

  // Fetch the same pincode-and-weight quote used by final checkout.
  useEffect(() => {
    if (!hasAvailableItems || updatingCart || !/^\d{6}$/.test(form.pincode)) {
      setShippingQuote(null);
      setShippingQuoteStatus({ checking: false, error: "" });
      return;
    }

    let cancelled = false;
    setShippingQuote(null);
    setShippingQuoteStatus({ checking: true, error: "" });

    const timer = setTimeout(() => {
      api.post("/checkout/shipping-quote", {
        delivery_pincode: form.pincode,
        cod: paymentMethod === "cod",
        cart_line_ids: JSON.parse(selectionKey),
      })
        .then((response) => {
          if (cancelled) return;
          setShippingQuote({
            ...response.data,
            _cartRevision: cartRevision,
            _paymentMethod: paymentMethod,
          });
          setShippingQuoteStatus({ checking: false, error: "" });
        })
        .catch((error) => {
          if (cancelled) return;
          setShippingQuote(null);
          setShippingQuoteStatus({
            checking: false,
            error:
              error.response?.data?.detail ||
              "Could not calculate shipping for this pincode.",
          });
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    form.pincode, paymentMethod, cartRevision, selectionKey,
    hasAvailableItems, updatingCart,
  ]);

  useEffect(() => {
    if (authLoading || user) return;

    window.sessionStorage.setItem(
      "nityavastra_login_return_to",
      "/checkout"
    );
    navigate("/login", {
      replace: true,
      state: { returnTo: "/checkout" },
    });
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen max-w-md mx-auto px-4 pt-[118px] sm:pt-[120px] lg:pt-[122px] pb-16 text-center">
        <p className="text-sm text-[#78716C]">Preparing secure checkout...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen max-w-md mx-auto px-4 pt-[118px] sm:pt-[120px] lg:pt-[122px] pb-16 text-center">
        <p className="mb-4">Your cart is empty.</p>
        <Button onClick={() => navigate("/shop")}>Shop Now</Button>
      </div>
    );
  }

  const shipping = Number(
    shippingQuote?.customer_shipping_inr || 0
  );
  const gstTotal = eligibleItems.reduce((sum, i) => {
    const gross = (i.product?.price || 0) * i.quantity;
    const rate = Number(i.product?.gst_rate ?? 5);
    const taxable = rate > 0 ? gross / (1 + rate / 100) : gross;
    return sum + Math.round((gross - taxable + Number.EPSILON) * 100) / 100;
  }, 0);
  const calculatedTotal =
    cartTotal + shipping - (couponApplied?.discount || 0);
  const total =
    paymentMethod === "cod"
      ? Math.ceil(calculatedTotal)
      : calculatedTotal;

  const applyCoupon = async () => {
    setCouponError("");
    if (!coupon.trim() || !hasAvailableItems || updatingCart || loading) return;
    try {
      const { data } = await api.post("/coupons/apply", {
        code: coupon.trim(),
        subtotal: cartTotal,
      });
      setCouponApplied({ ...data, _cartRevision: cartRevision });
      toast.success(`Applied ${data.code} — you saved ₹${data.discount}`);
    } catch (e) {
      setCouponApplied(null);
      setCouponError(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCoupon("");
    setCouponError("");
  };

  const submit = async (e, resumeRequest = null) => {
    e.preventDefault();
    if (CHECKOUT_COMING_SOON) {
      toast.info("Coming soon! Ordering will open shortly.");
      return;
    }
    if (loading || updatingCart) return;
    if (!resumeRequest) {
      if (!hasAvailableItems || updatingCart || loading) {
        return toast.error("Add an in-stock item before continuing.");
      }
      if (paymentMethod === "cod" && codCheck.available !== true) {
        return toast.error("Cash on delivery is unavailable.");
      }
      if (!form.name || !form.phone || !form.address || !form.city || !form.pincode) {
        return toast.error("Please fill in all address fields");
      }
      if (
        !shippingQuote ||
        shippingQuote.delivery_pincode !== form.pincode ||
        shippingQuote._cartRevision !== cartRevision ||
        shippingQuote._paymentMethod !== paymentMethod
      ) {
        return toast.error(
          shippingQuoteStatus.error ||
          "Please wait while shipping is calculated."
        );
      }

    }

    setLoading(true);
    // COD flow
    if (paymentMethod === "cod" && !resumeRequest) {
      try {
        const { data } = await api.post("/checkout/cod", {
          shipping_address: form,
          cart_line_ids: JSON.parse(selectionKey),
          coupon_code: couponApplied ? couponApplied.code : null,
        });
        await refreshCart();
        toast.success("Order placed! Pay on delivery.");
        navigate(`/payment/success?order_id=${data.order_id}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not place order");
        setLoading(false);
      }
      return;
    }
    const ok = await loadRazorpay();
    if (!ok) {
      setLoading(false);
      return toast.error("Payment gateway failed to load. Check your connection.");
    }
    try {
      const checkoutRequest = resumeRequest || {
        shipping_address: form,
        cart_line_ids: JSON.parse(selectionKey),
        coupon_code: couponApplied ? couponApplied.code : null,
      };
      const { data } = await api.post("/checkout", checkoutRequest);
      setPendingPayment({
        order_id: data.order_id,
        amount_inr: data.amount / 100,
        inventory_expires_at: data.inventory_expires_at,
        request: checkoutRequest,
      });

      const deadline = Date.parse(data.inventory_expires_at);
      const serverTime = Date.parse(data.server_time);
      const durationMs = deadline - serverTime;
      if (!Number.isFinite(durationMs) || durationMs <= 0) {
        setLoading(false);
        toast("This payment session expired. Check your orders before trying again.");
        return;
      }
      const remainingSeconds = Math.min(300, Math.ceil(durationMs / 1000));
      setReservation({
        orderId: data.order_id,
        startedAt: performance.now(),
        durationMs: Math.min(durationMs, 300000),
      });
      setSecondsLeft(remainingSeconds);

      const options = {
        timeout: remainingSeconds,
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Nityavastra",
        description: "Sacred Weaves · Everyday Grace",
        image:
          "https://customer-assets-lqy194kg.emergentagent.net/job_nityavastra-shop/artifacts/dirh3az1_Updated%20Logo%20Nityavastra.png",
        order_id: data.razorpay_order_id,
        prefill: {
          name: data.customer.name,
          email: data.customer.email,
          contact: data.customer.contact,
        },
        notes: {
          shipping_address: [
            checkoutRequest.shipping_address.address,
            checkoutRequest.shipping_address.city,
            checkoutRequest.shipping_address.state,
            checkoutRequest.shipping_address.pincode,
          ].filter(Boolean).join(", "),
        },
        theme: {
          color: "#7C1F30",
        },
        handler: async (response) => {
          setPendingPayment(null);
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: data.order_id,
            });
            await refreshCart();
            navigate(`/payment/success?order_id=${data.order_id}`);
          } catch {
            toast("Payment confirmation needs checking. Do not pay again for this order.");
            navigate(`/payment/success?order_id=${data.order_id}`);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed: " + (resp.error?.description || "Try again"));
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      await refreshCart();
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen max-w-6xl mx-auto px-4 sm:px-8 pt-[110px] sm:pt-[112px] lg:pt-[122px] pb-12 lg:pb-16">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2A1508] mb-10">Checkout</h1>

      <button
        type="button"
        onClick={() => navigate("/shop")}
        className="mb-5 text-sm text-[#7C1F30] underline"
      >
        ← Continue Shopping
      </button>
      {!CHECKOUT_COMING_SOON && pendingPayment && (
        <div role="status" data-testid="reserved-payment-panel"
          className="mb-6 rounded-lg border border-[#D8CBBE] bg-[#FAF3E7] p-4">
          <p className="font-medium">Your earlier checkout has reserved stock.</p>
          <p className="mt-1 text-sm">
            Resume the original order for {formatCheckoutINR(pendingPayment.amount_inr)}.
            Its original items, address, coupon and payment deadline still apply.
          </p>
          <Button type="button"
            disabled={loading || updatingCart}
            data-testid="resume-reserved-payment"
            onClick={(event) => submit(event, pendingPayment.request)}
            className="mt-3 bg-[#7C1F30] text-white">
            Resume reserved payment
          </Button>
          <button type="button"
            onClick={() => navigate("/account?tab=orders")}
            className="ml-4 text-sm underline">
            View My Orders
          </button>
        </div>
      )}
      {reservationError && (
        <p role="alert" className="mb-4 text-sm text-red-700">
          {reservationError}
        </p>
      )}
      {!hasAvailableItems && (
        <p role="status" data-testid="checkout-stock-notice"
          className="mb-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          None of your cart items are currently available to purchase.
          Add an in-stock product to continue. Your address details are preserved.
        </p>
      )}
      {hasAvailableItems && eligibleItems.length < items.length && (
        <p role="status"
          className="mb-6 rounded-lg border border-[#D8CBBE] bg-[#FAF3E7] p-4 text-sm">
          Only available items are included in this order.
          Other items remain in your cart and will not be charged.
        </p>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <fieldset
          disabled={!hasAvailableItems || updatingCart || loading}
          className="lg:col-span-2 min-w-0 space-y-6 disabled:opacity-60"
        >
          <div className="bg-white border border-[#E7E5E4] rounded-lg p-6">
            <h2 className="font-serif text-xl mb-6">Shipping Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Full Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="ship-name"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="ship-phone"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Pincode</Label>
                <Input
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                  data-testid="ship-pincode"
                  className="bg-white"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="ship-address"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="ship-city"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  data-testid="ship-state"
                  className="bg-white"
                />
              </div>
              <div className="md:col-span-2">
                <Label>GSTIN (Optional — for business invoices)</Label>
                <Input
                  value={form.gstin}
                  onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                  data-testid="ship-gstin"
                  placeholder="e.g. 07AAAAA0000A1Z5"
                  className="bg-white font-mono"
                />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E7E5E4] rounded-lg p-6">
            <h2 className="font-serif text-xl mb-4">Payment Method</h2>
            {reservation && (
              <div
                role="status"
                data-testid="reservation-countdown"
                className="mb-4 rounded-md border border-[#D8CBBE] bg-[#FAF3E7] p-3 text-sm"
              >
                {secondsLeft > 0 ? (
                  <>
                    Stock reserved for this checkout:{" "}
                    <strong>
                      {Math.floor(secondsLeft / 60)}:
                      {String(secondsLeft % 60).padStart(2, "0")}
                    </strong>
                    <p className="mt-1 text-xs">
                      Complete payment within this time. Retrying does not extend it.
                    </p>
                  </>
                ) : (
                  <>
                    Payment session expired.
                    <p className="mt-1 text-xs">
                      Unpaid stock is released after payment verification.
                      If you completed payment, check your orders and do not pay again.
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/account?tab=orders")}
                  className="mt-2 text-xs underline"
                >
                  View my orders
                </button>
              </div>
            )}
            <div className="space-y-3 mb-5">
              <button
                type="button"
                onClick={() => setPaymentMethod("razorpay")}
                data-testid="pay-method-razorpay"
                className={`w-full text-left p-4 rounded-md border-2 transition-colors ${paymentMethod === "razorpay" ? "border-[#7C1F30] bg-[#FAF3E7]" : "border-[#E7E5E4] bg-white"}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${paymentMethod === "razorpay" ? "border-[#7C1F30]" : "border-[#E7E5E4]"} flex items-center justify-center`}>
                    {paymentMethod === "razorpay" && <div className="w-2 h-2 rounded-full bg-[#7C1F30]" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">Pay Online (Razorpay)</div>
                    <div className="text-xs text-[#78716C] mt-0.5">UPI · Cards · Net Banking · Wallets</div>
                  </div>
                  <div className="flex items-center gap-2 text-[#7C1F30]">
                    <Smartphone className="h-4 w-4" /> <CreditCard className="h-4 w-4" /> <Building2 className="h-4 w-4" />
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => codCheck.available === true && setPaymentMethod("cod")}
                disabled={codCheck.available !== true}
                data-testid="pay-method-cod"
                className={`w-full text-left p-4 rounded-md border-2 transition-colors ${paymentMethod === "cod" ? "border-[#7C1F30] bg-[#FAF3E7]" : "border-[#E7E5E4] bg-white"} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${paymentMethod === "cod" ? "border-[#7C1F30]" : "border-[#E7E5E4]"} flex items-center justify-center`}>
                    {paymentMethod === "cod" && <div className="w-2 h-2 rounded-full bg-[#7C1F30]" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">Cash on Delivery</div>
                    <div className="text-xs mt-0.5" data-testid="cod-availability">
                      {!form.pincode
                        ? <span className="text-[#78716C]">Enter pincode to check availability</span>
                        : codCheck.checking
                        ? <span className="text-[#78716C]">Checking availability...</span>
                        : codCheck.available === true
                        ? <span className="text-green-700">✓ Available at your pincode</span>
                        : codCheck.available === false
                        ? <span className="text-red-600">{codCheck.reason}</span>
                        : <span className="text-[#78716C]">—</span>}
                    </div>
                  </div>
                </div>
              </button>
            </div>
            <div className="flex items-start gap-3 text-sm text-[#2A1508]/80 bg-[#FAF3E7] p-4 rounded-md border border-[#E7E5E4]">
              <ShieldCheck className="h-5 w-5 text-[#7C1F30] shrink-0 mt-0.5" />
              <div>
                {paymentMethod === "razorpay"
                  ? "Powered by Razorpay. Card & UPI details are encrypted end-to-end."
                  : "Pay the delivery agent in cash at your doorstep. Please have exact change ready."}
              </div>
            </div>
          </div>
        </fieldset>

        <aside className="lg:col-span-1">
          <div className="bg-white border border-[#E7E5E4] rounded-lg p-6 sticky top-28">
            <h2 className="font-serif text-xl mb-6">Order Summary</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto mb-6 pr-2">
              {items.map((i) => (
                <div
                  key={i.line_id || `${i.product_id}-${i.variant_sku || "base"}`}
                  className="flex gap-3 border-b border-[#E7E5E4] pb-3 text-sm"
                >
                  {i.product?.image ? (
                    <img src={i.product.image} alt=""
                      className="h-16 w-12 rounded object-contain" />
                  ) : (
                    <div className="h-16 w-12 shrink-0 rounded bg-[#F5F5F4]" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {i.product?.name || "Unavailable product"}
                    </p>
                    {i.variant_sku && (
                      <p className="break-all text-xs text-[#78716C]">
                        {i.variant?.color || ""} {i.variant?.size || ""}
                      </p>
                    )}
                    <p className={`mt-1 text-xs ${
                      i.checkout_eligible ? "text-green-700" : "text-red-700"
                    }`}>
                      {i.availability_message || "Checking stock availability"}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button type="button"
                        aria-label={`Reduce quantity of ${i.product?.name}`}
                        disabled={updatingCart || loading || i.quantity <= 1}
                        onClick={() => changeCheckoutQuantity(i, i.quantity - 1)}
                        className="rounded border px-2 py-1 disabled:opacity-40"
                      >−</button>
                      <span aria-label="Quantity">{i.quantity}</span>
                      <button type="button"
                        aria-label={`Increase quantity of ${i.product?.name}`}
                        disabled={
                          updatingCart || loading ||
                          !i.available_quantity ||
                          i.quantity >= i.available_quantity
                        }
                        onClick={() => changeCheckoutQuantity(i, i.quantity + 1)}
                        className="rounded border px-2 py-1 disabled:opacity-40"
                      >+</button>
                      <button type="button"
                        disabled={updatingCart || loading}
                        onClick={() => changeCheckoutQuantity(i, 0)}
                        className="text-xs text-[#7C1F30] underline disabled:opacity-40"
                      >Remove</button>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    {i.checkout_eligible
                      ? formatCheckoutINR(Number(i.product?.price || 0) * i.quantity)
                      : <span className="text-xs text-[#78716C]">Not included</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2 text-sm border-t border-[#E7E5E4] pt-4">
              <div className="flex justify-between">
                <span className="text-[#78716C]">Subtotal</span>
                <span>{formatCheckoutINR(cartTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">GST (included)</span>
                <span>{formatCheckoutINR(gstTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Shipping</span>
                <span>
                  {!hasAvailableItems
                    ? "—"
                    : shippingQuoteStatus.checking
                    ? "Calculating..."
                    : shippingQuoteStatus.error
                    ? "Unavailable"
                    : !shippingQuote
                    ? "Enter pincode"
                    : shipping === 0
                    ? "Free"
                    : formatCheckoutINR(shipping)}
                </span>
              </div>
              {shippingQuote && (
                <div
                  className="flex justify-between gap-3 text-xs text-[#78716C]"
                  data-testid="shipping-quote-details"
                >
                  <span>{shippingQuote.courier_name}</span>
                  <span className="text-right">
                    {shippingQuote.shipping_paid_by === "nityavastra"
                      ? "Paid by Nityavastra"
                      : "Paid by customer"}
                  </span>
                </div>
              )}
              {shippingQuoteStatus.error && (
                <p className="text-xs text-red-600">
                  {shippingQuoteStatus.error}
                </p>
              )}
              {couponApplied && (
                <div className="flex justify-between text-green-700" data-testid="coupon-discount-row">
                  <span>Coupon ({couponApplied.code})</span>
                  <span>−{formatCheckoutINR(couponApplied.discount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-[#E7E5E4] font-medium text-base">
                <span>Total</span>
                <span data-testid="checkout-total">{formatCheckoutINR(total)}</span>
              </div>
            </div>

            {/* Coupon input */}
            {couponApplied ? (
              <div className="mt-5 p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-green-800">{couponApplied.code} applied</div>
                  <div className="text-xs text-green-700">You saved {formatCheckoutINR(couponApplied.discount)}</div>
                </div>
                <button
                  type="button"
                  onClick={removeCoupon}
                  disabled={!hasAvailableItems || updatingCart || loading}
                  data-testid="remove-coupon-btn"
                  className="text-xs text-green-700 underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="mt-5">
                <div className="flex gap-2">
                  <Input
                    value={coupon}
                    disabled={!hasAvailableItems || updatingCart || loading}
                    onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); }}
                    placeholder="Coupon code"
                    data-testid="coupon-input"
                    className="bg-white text-xs uppercase font-mono"
                  />
                  <Button
                    type="button"
                    onClick={applyCoupon}
                    disabled={!hasAvailableItems || updatingCart || loading}
                    variant="outline"
                    data-testid="apply-coupon-btn"
                    className="rounded-full text-xs"
                  >
                    Apply
                  </Button>
                </div>
                {couponError && <p className="text-xs text-red-600 mt-1" data-testid="coupon-error">{couponError}</p>}
                <p className="text-[10px] text-[#78716C] mt-1.5">Try <span className="font-mono">NITYA20</span> for 20% off (min ₹1,499)</p>
              </div>
            )}
            <Button
              type="submit"
              disabled={
                CHECKOUT_COMING_SOON ||
                loading ||
                updatingCart ||
                !hasAvailableItems ||
                shippingQuoteStatus.checking ||
                !shippingQuote ||
                shippingQuote._cartRevision !== cartRevision ||
                shippingQuote._paymentMethod !== paymentMethod ||
                (paymentMethod === "cod" && codCheck.available !== true)
              }
              data-testid="place-order-btn"
              className="w-full mt-6 bg-[#7C1F30] hover:bg-[#8D2A3D] text-white rounded-full py-6 uppercase text-xs tracking-[0.2em]"
            >
              {CHECKOUT_COMING_SOON
                ? "Coming Soon"
                : loading
                ? "Processing..."
                : paymentMethod === "cod"
                ? `Place Order · ${formatCheckoutINR(total)}`
                : `Pay ${formatCheckoutINR(total)}`}
            </Button>
            <p className="text-[10px] text-center text-[#78716C] mt-3">
              {CHECKOUT_COMING_SOON
                ? "We’re getting ready! Ordering will open soon."
                : "By placing this order you agree to our terms."}
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
